/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { TablePagination } from 'src/core/components/TablePagination';
import { SortKeyType } from 'src/core/components/TableProps';
import { useAnnounced } from 'src/core/hooks/useAnnounced';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { usePagination } from 'src/core/hooks/usePagination';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { Sublist } from 'src/luis/projects/entities/types/Sublist';
import { ListEntityStore } from 'src/luis/types/stores/entities/ListEntityStore';
import { SublistStore } from 'src/luis/types/stores/entities/SublistStore';
import {
    ImportSublistModalDto,
    ImportSublistsModal
} from 'src/luis/verticals/entityDetails/components/listEntityDetails/ImportSublistModal';
import { SublistsTable } from 'src/luis/verticals/entityDetails/components/listEntityDetails/SublistsTable';
import { NewSublistsTableCommandBar } from 'src/luis/verticals/entityDetails/components/listEntityDetails/SublistsTableCommandBar';

export type ListEntityDetailsProps = {
    /**
     * The list entity store to display its details.
     */
    entityStore: ListEntityStore;
};

const messages = defineMessages({
    deleteSublistTitle: {
        id: 'listEntityDetails.listEntityDetails.deleteSublistTitle',
        defaultMessage: 'Delete sublist?'
    },
    deleteSublistDescription: {
        id: 'listEntityDetails.listEntityDetails.deleteSublistDescription',
        defaultMessage:
            'Are you sure you want to delete the selected sublist(s)? Deleting a sublist will also automatically remove all associated labels from the utterances.'
    },
    deletingSublist: {
        id: 'listEntityDetails.listEntityDetails.deletingSublist',
        defaultMessage: 'Deleting sublist'
    },
    importingSublists: {
        id: 'listEntityDetails.listEntityDetails.importingSublists',
        defaultMessage: 'Importing sublists'
    },
    importingSublistsFailureTitle: {
        id: 'listEntityDetails.listEntityDetails.importingSublistsFailureTitle',
        defaultMessage: 'Failed to import sublists'
    },
    importingSublistsFailureContent: {
        id: 'listEntityDetails.listEntityDetails.importingSublistsFailureContent',
        defaultMessage: 'An error ocurred. Invalid file format and/or canonical value duplications.'
    },
    addingSublist: {
        id: 'listEntityDetails.listEntityDetails.addingSublist',
        defaultMessage: 'Adding sublist'
    },
    updatingSublist: {
        id: 'listEntityDetails.listEntityDetails.updatingSublist',
        defaultMessage: 'Updating sublists'
    },
    addingValue: {
        id: 'listEntityDetails.listEntityDetails.addingValue',
        defaultMessage: 'Adding value'
    },
    deleteSublistOperationCompleted: {
        id: 'listEntityDetails.listEntityDetails.deleteSublistOperationCompleted',
        defaultMessage: 'Sublist successfully deleted.'
    },
    deleteSublistOperationFailed: {
        id: 'listEntityDetails.listEntityDetails.deleteSublistOperationFailed',
        defaultMessage: 'Failed to delete one or more sublist(s).'
    },
    addSublistOperationCompleted: {
        id: 'listEntityDetails.listEntityDetails.addSublistOperationCompleted',
        defaultMessage: 'Sublist successfully added.'
    },
    importValuesOperationCompleted: {
        id: 'listEntityDetails.listEntityDetails.importValuesOperationCompleted',
        defaultMessage: 'Values successfully imported.'
    },
    updateSublistOperationCompleted: {
        id: 'listEntityDetails.listEntityDetails.updateSublistOperationCompleted',
        defaultMessage: 'Sublist successfully updated.'
    },
    addSublistValueOperationCompleted: {
        id: 'listEntityDetails.listEntityDetails.addSublistValueOperationCompleted',
        defaultMessage: 'Sublist value successfully added.'
    },
    deleteSublistValueOperationCompleted: {
        id: 'listEntityDetails.listEntityDetails.deleteSublistValueOperationCompleted',
        defaultMessage: 'Sublist value successfully deleted.'
    },
    deletingValue: {
        id: 'listEntityDetails.listEntityDetails.deletingValue',
        defaultMessage: 'Deleting value.'
    },
    deleteValueFailureMessage: {
        id: 'listEntityDetails.listEntityDetails.deleteValueFailureMessage',
        defaultMessage: 'Failed to delete value.'
    }
});

const defaultModalStates = {
    isImportModalHidden: true,
    isDeleteSublistModalHidden: true
};

/**
 * A table displaying the sublists of a list entity along with the associated commands.
 */
export const ListEntityDetails = observer((props: ListEntityDetailsProps) => {
    const { entityStore } = props;

    const { dispatcher, rootStore } = useGlobalState();
    const { localize } = useLocalization();

    const { announceAction, announcedComponent } = useAnnounced();

    const { selection, selectedItems } = useSelection<Sublist>();
    const [sortKey, setSortKey] = React.useState<SortKeyType<SublistStore>>({ key: 'name', order: 'asc' });

    const [query, setQuery] = React.useState('');
    const [modalsState, setModalsState] = useUpdateState(defaultModalStates);

    const { userRole, displayedUserRole } = rootStore.serviceKey;

    const filteredSublists = orderBy(
        substringSearchStrategy(
            entityStore.sublists,
            query,
            'name',
            item => item.name.includes(query) || !!substringSearchStrategy(item.values, query).length
        ),
        [sortKey.key],
        [sortKey.order]
    );
    const { currentPage, setCurrentPage, pageItems, pageCount } = usePagination(
        filteredSublists,
        LuisConstants.ResourceLimits.DefaultPageSize
    );

    const onCompleteTracker = (announceActionMsg: string) => {
        setModalsState(defaultModalStates);
        announceAction(announceActionMsg);
        selection.setAllSelected(false);
    };

    const deleteSublistTracker = useProgressTracker({
        onComplete: () => {
            onCompleteTracker(localize(messages.deleteSublistOperationCompleted));
        }
    });

    const addSublistTracker = useProgressTracker({
        onComplete: () => {
            onCompleteTracker(localize(messages.addSublistOperationCompleted));
        }
    });

    const importValuesTracker = useProgressTracker({
        onComplete: () => {
            onCompleteTracker(localize(messages.importValuesOperationCompleted));
        }
    });

    const updateSublistTracker = useProgressTracker({
        onComplete: () => {
            onCompleteTracker(localize(messages.updateSublistOperationCompleted));
        }
    });

    const addSublistValueTracker = useProgressTracker({
        onComplete: () => {
            announceAction(localize(messages.addSublistValueOperationCompleted));
        }
    });

    const deleteSublistValueTracker = useProgressTracker({
        onComplete: () => {
            announceAction(localize(messages.deleteSublistValueOperationCompleted));
        }
    });

    /**
     * Deletes the selected sublists.
     */
    const onDeleteSublists = () => {
        dispatcher.dispatch('deleteSublistsForListEntity', {
            id: entityStore.id,
            sublists: selectedItems,
            tracker: deleteSublistTracker,
            toastData: {
                running: {
                    title: localize(messages.deletingSublist)
                },
                completed: {
                    title: localize(messages.deleteSublistOperationCompleted)
                },
                failed: {
                    title: localize(messages.deleteSublistOperationFailed)
                }
            }
        });
    };

    /**
     * Imports sublists from JSON file.
     */
    const onImportValuesFile = (dto: ImportSublistModalDto) => {
        dispatcher.dispatch('importSublistValues', {
            importedFile: dto.sublistFile,
            id: entityStore.id,
            tracker: importValuesTracker,
            toastData: {
                running: { title: localize(messages.importingSublists) },
                failed: {
                    title: localize(messages.importingSublistsFailureTitle),
                    content: localize(messages.importingSublistsFailureContent)
                }
            }
        });
    };

    /**
     * Adds new sublist.
     *
     * @param sublistName The new sublist name.
     */
    const onAddNewSublist = (sublistName: string) =>
        dispatcher.dispatch('addSublistToListEntity', {
            id: entityStore.id,
            sublist: { name: sublistName, values: [] },
            tracker: addSublistTracker,
            toastData: { running: { title: localize(messages.addingSublist) } }
        });

    /**
     * Renames the given sublist.
     *
     * @param sublistId The sublist id to be renamed.
     * @param newName The new name of the sublist.
     */
    const onRenameSublist = (sublistId: string, newName: string) => {
        const { name, ...sublist } = entityStore.sublists.find(list => list.id === sublistId);
        dispatcher.dispatch('updateSublistForListEntity', {
            id: entityStore.id,
            sublist: { name: newName, ...sublist },
            tracker: updateSublistTracker,
            toastData: { running: { title: localize(messages.updatingSublist) } }
        });
    };

    /**
     * Adds the given values to the given sublist.
     *
     * @param sublistId The sublist id to add the values in.
     * @param values The values to be added.
     */
    const onAddSublistValues = (sublistId: string, values: string[]) => {
        const sublist = entityStore.sublists.find(list => list.id === sublistId);
        const filteredValues = values.filter(value => !sublist.values.find(sV => sV.toLocaleLowerCase() === value.toLocaleLowerCase()));
        if (filteredValues.length) {
            dispatcher.dispatch('updateSublistForListEntity', {
                id: entityStore.id,
                sublist: { ...sublist, values: [...sublist.values, ...filteredValues] },
                tracker: addSublistValueTracker,
                toastData: { running: { title: localize(messages.addingValue) } }
            });
        }
    };

    /**
     * Deletes The given values for the given sublist.
     *
     * @param sublistId The sublist id to delete values from.
     * @param values The values to be deleted.
     */
    const onDeleteSublistValues = (sublistId: string, values: string[]) => {
        const sublist = entityStore.sublists.find(list => list.id === sublistId);
        dispatcher.dispatch('updateSublistForListEntity', {
            id: entityStore.id,
            tracker: deleteSublistValueTracker,
            sublist: { ...sublist, values: sublist.values.filter(val => !values.includes(val)) },
            toastData: {
                running: { title: localize(messages.deletingValue) },
                failed: { title: localize(messages.deleteValueFailureMessage) }
            }
        });
    };

    return (
        <Stack>
            <NewSublistsTableCommandBar
                selectedItems={selectedItems}
                query={query}
                onQueryChange={setQuery}
                onDeleteSublists={() => setModalsState({ isDeleteSublistModalHidden: false })}
                onImportValues={() => setModalsState({ isImportModalHidden: false })}
                userRole={userRole}
                displayedUserRole={displayedUserRole}
            />

            <DeletePromptModal
                hidden={modalsState.isDeleteSublistModalHidden}
                onDelete={onDeleteSublists}
                dialogContentProps={{
                    title: localize(messages.deleteSublistTitle),
                    subText: localize(messages.deleteSublistDescription)
                }}
                onDismiss={() => setModalsState({ isDeleteSublistModalHidden: true })}
            />

            <ImportSublistsModal
                hidden={modalsState.isImportModalHidden}
                onSubmit={onImportValuesFile}
                onDismiss={() => setModalsState({ isImportModalHidden: true })}
            />

            <SublistsTable
                sortKey={sortKey}
                items={pageItems}
                selection={selection}
                areItemsFiltered={Boolean(query)}
                overallItemsCount={filteredSublists.length}
                onAddNewSublist={onAddNewSublist}
                onRenameSublist={onRenameSublist}
                onAddSublistValues={onAddSublistValues}
                onDeleteSublistValues={onDeleteSublistValues}
                onSortColumn={c => {
                    setSortKey({ key: c.key as keyof SublistStore, order: c.isSortedDescending ? 'asc' : 'desc' });
                    selection.setAllSelected(false);
                }}
                onCancelSorting={() => {
                    setSortKey({ key: undefined, order: undefined });
                }}
                userRole={userRole}
            />

            {announcedComponent}

            <TablePagination pageCount={pageCount} currentPage={currentPage} onPageChange={p => setCurrentPage(p.selected)} />
        </Stack>
    );
});
